import {
  FunctionComponent,
  useCallback,
  useMemo,
} from 'react';

import { Graph, isPredicted } from '@intoto-dev/bibliotheca-graph';
import { observationTypeToSeriesProperties } from '@intoto-dev/utils-observation-helpers';
import { DataPoint, GraphSeries } from '@intoto-dev/bibliotheca-graph/lib/types';
import { ObservationType } from '@intoto-dev/utils-observation-helpers/lib/types';

import useFetch from '../../hooks/useFetch';
import en from '../../locales/en.json';
import nb from '../../locales/nb.json';
import useParams from '../../hooks/useParams';
import { useDateLocale } from '../../hooks/useDateLocale';
import isFutureDataDisabled from '../../utils/isFutureDataDisabled';

interface ObservationContainerProps {
  observation: {
    id: string;
    type: ObservationType;
  };
  days: number;
  specificity?: string;
  offset?: number;
}

interface ObservationData {
  entities: DataPoint[];
}

const ObservationContainer: FunctionComponent<ObservationContainerProps> = ({
  observation,
  days,
  specificity = 'overview',
  offset = 0,
}) => {
  const params = useParams();
  const {
    isLoading,
    error,
    data,
  } = useFetch<ObservationData>(
    `observation/${observation.id}?days=${days}&specificity=${specificity}`,
  );

  const entities = useMemo(() => {
    if (data) {
      return data.entities
        .filter((d) => {
          if (isFutureDataDisabled()) {
            return !isPredicted(d) && new Date(d.date) < new Date();
          }

          return true;
        })
        .map((v) => ({
          ...v,
          missing: false,
          value: v.value + offset,
          minValue: typeof v.minValue !== 'undefined' ? v.minValue + offset : undefined,
          maxValue: typeof v.maxValue !== 'undefined' ? v.maxValue + offset : undefined,
        }));
    }

    return [];
  }, [data, offset]);

  const lang = useMemo(() => {
    const paramLang = params.get('lang');

    if (paramLang === 'nb') {
      return 'nb';
    }

    return 'en';
  }, [params]);

  const locale = useDateLocale(lang);

  const t = useCallback((key: keyof typeof nb): string => {
    if (lang === 'nb') {
      return nb[key];
    }

    return en[key];
  }, [lang]);

  if (isLoading || !data) {
    return (
      <div className="centered">
        Loading observation...
      </div>
    );
  }

  if (error) {
    return (
      <div className="centered">
        <h2>Whoops, something went wrong</h2>
        {error.message}
      </div>
    );
  }

  const series: GraphSeries[] = [{
    ...observationTypeToSeriesProperties(observation.type, lang),
    key: 'water-level',
    name: t('water-level'),
    data: entities,
  }];

  return (
    <Graph
      series={series}
      lang={lang}
      locale={locale}
      t={t}
      tooltip
    />
  );
};

export default ObservationContainer;
